import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ImageReminderCards.css";
import html2canvas from "html2canvas";

const ImageReminderCards = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [gridSize, setGridSize] = useState(4);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/products/download`
      );
      setAllProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = allProducts.filter((product) =>
        product.brand_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
      setShowDropdown(true);
    } else {
      setFilteredProducts([]);
      setShowDropdown(false);
    }
  }, [searchTerm, allProducts]);

  const handleAddProduct = (product) => {
    if (selectedProducts.length < gridSize) {
      setSelectedProducts([...selectedProducts, product]);
      setSearchTerm("");
      setShowDropdown(false);
    } else {
      alert("Maximum products added!");
    }
  };

  const handleRemoveProduct = (index) => {
    const newSelectedProducts = [...selectedProducts];
    newSelectedProducts.splice(index, 1);
    setSelectedProducts(newSelectedProducts);
  };

  const DropdownItemStyle = {
    padding: "10px 15px",
    borderBottom: "1px solid #eee",
    cursor: "pointer",
    backgroundColor: "#fff",
    color: "#333",
  };

  const handleDownload = () => {
    const gridElement = document.querySelector(".product-grid");
    const clone = gridElement.cloneNode(true);
    const emptyCells = Array.from(clone.querySelectorAll(".empty"));
    emptyCells.forEach((cell) => cell.remove());

    document.body.appendChild(clone); // Temporarily add clone to document to manipulate
    clone.style.display = "grid";
    clone.style.gridTemplateColumns = `repeat(2, 1fr)`;
    clone.style.width = selectedProducts.length <= 4 ? "700px" : "1000px";
    clone.style.margin = "50px"; // Add margin around the grid
    clone.style.padding = "20px"; // Additional padding
    clone.style.backgroundColor = "#ffffff"; // Ensure background is white for download

    const images = Array.from(clone.querySelectorAll("img"));

    const checkImagesLoaded = (images) => {
      return new Promise((resolve) => {
        let loadedCount = 0;
        images.forEach((img) => {
          if (img.complete && img.naturalHeight !== 0) {
            loadedCount++;
          } else {
            img.onload = () => {
              loadedCount++;
              if (loadedCount === images.length) {
                resolve();
              }
            };
          }
        });
        if (loadedCount === images.length) {
          resolve();
        }
      });
    };

    checkImagesLoaded(images).then(() => {
      html2canvas(clone, {
        scale: 2, // Increase scale factor for higher resolution
        useCORS: true, // Ensure images are loaded correctly
        allowTaint: false,
        logging: true,
      }).then((canvas) => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = "image-reminder-cards.png";
        link.click();
        document.body.removeChild(clone); // Remove clone after download
      });
    });
  };

  const getImagePath = (company, imageName) => {
    let basePath = "";

    switch (company) {
      case "Trumac":
        basePath = "/app_admin/assets/images/products";
        break;
      case "Camrut":
        basePath = "/camrut_app_admin/assets/images/products";
        break;
      case "Medisync":
        basePath = "/medisync_app_admin/assets/images/products";
        break;
      case "Cialto Drugs":
        basePath = "/cialto_app_admin/assets/images/products";
        break;
      case "Pythus Pharma":
        basePath = "/pythus_app_admin/assets/images/products";
        break;
      default:
        basePath = "/images";
    }

    const isDevelopment =
      !process.env.NODE_ENV || process.env.NODE_ENV === "development";

    return isDevelopment
      ? `https://trumachealthcare.net${basePath}/${imageName}`
      : `${basePath}/${imageName}`;
  };

  if (loading) {
    return <div className="loading">Data is loading...</div>;
  }

  return (
    <div className="container">
      <h1>Make Your Own Image Reminder Cards</h1>
      <div className="reminder-cards-top-bar">
        <label>
          Select Number of Products:
          <select
            value={gridSize}
            onChange={(e) => setGridSize(Number(e.target.value))}
          >
            {Array.from({ length: 8 }, (_, i) => (i + 1) * 2).map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={handleDownload}
          disabled={selectedProducts.length === 0}
        >
          Download as Image
        </button>
      </div>
      <label>
        Add Product in Reminder Card:
        <input
          type="text"
          placeholder="Type Brand Name of Product to add..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </label>
      {showDropdown && (
        <div className="dropdown">
          {filteredProducts.map((product, index) => (
            <div
              key={index}
              className="dropdown-item"
              style={DropdownItemStyle}
              onClick={() => handleAddProduct(product)}
            >
              {product.brand_name}
            </div>
          ))}
        </div>
      )}
      <div className="product-grid">
        {Array.from({ length: gridSize }).map((_, index) => (
          <div
            key={index}
            className={`grid-cell-1 ${selectedProducts[index] ? "" : "empty"}`}
          >
            {selectedProducts[index] ? (
              <>
                <span
                  className="remove-icon"
                  onClick={() => handleRemoveProduct(index)}
                >
                  &times;
                </span>
                <div className="image-container">
                  <img
                    src={getImagePath(
                      selectedProducts[index].company,
                      selectedProducts[index].product_image
                    )}
                    alt={selectedProducts[index].brand_name}
                    className="product-image"
                  />
                </div>
              </>
            ) : (
              "Add Product"
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageReminderCards;
